// BETA 13.84 — BOT FARM DASHBOARD
// =============================================================================
// User direction (cycle-5 prep): "i would like it to max it out so i can see
// each game. and one toggle to make it invisible to me. like one canvas with
// all characters running at once."
//
// This page renders 10 game iframes in a 5×2 grid (designed for 5140×1440
// ultrawide — each tile ~1028×720 ≈ 720p). Each iframe loads
// /?botfarm=true&character=X&build=Y&runs=N&muted=true so it auto-bots without
// user interaction. The dashboard receives postMessage updates from each iframe
// (`farm-status` / `farm-progress` / `farm-complete`) and aggregates them into
// a master CSV. Mute toggle + per-character checkboxes + runs-per-char slider.
// "Show Games" toggle hides all tiles via display:none for headless-feel mode.
// =============================================================================

import { useState, useEffect, useRef, useCallback } from 'react';

const CHARACTERS = [
  { id: 'cowboy',   build: 'rpk_suppressor', label: 'Cowboy' },
  { id: 'killa',    build: 'rpk_suppressor', label: 'Killa' },
  { id: 'viking',   build: 'rpk_suppressor', label: 'Viking' },
  { id: 'sponge',   build: 'spatula_melee',  label: 'Sponge' },
  { id: 'mr_silly', build: 'fireball_kite',  label: 'Mr. Silly' },
  { id: 'wizard',   build: 'fireball_kite',  label: 'Wizard' },
  { id: 'lebron',   build: 'peanut_sniper',  label: 'LeBron' },
  { id: 'tactical', build: 'peanut_sniper',  label: 'Tactical' },
  { id: 'chad',     build: 'chad_aura',      label: 'Chad' },
  { id: 'pirate',   build: 'tank_sustain',   label: 'Pirate' },
] as const;

type FarmStatus = 'idle' | 'starting' | 'running' | 'complete' | 'error';
interface TileState {
  status: FarmStatus;
  completed: number;
  target: number;
  inFlightSurv: number;
  elapsedSec: number;
  fps: number;       // BETA 13.85: per-tile rAF FPS (proves browser isn't throttling)
  memMB: number;     // BETA 13.85: per-tile JS heap MB (Chrome only — Safari/FF show 0)
  csv?: string;
  error?: string;
}

function createInitialTileState(): TileState {
  return { status: 'idle', completed: 0, target: 0, inFlightSurv: 0, elapsedSec: 0, fps: 0, memMB: 0 };
}

export default function BotFarm() {
  const [runsPerChar, setRunsPerChar] = useState(5);
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(CHARACTERS.map(c => [c.id, true])),
  );
  const [tiles, setTiles] = useState<Record<string, TileState>>(() =>
    Object.fromEntries(CHARACTERS.map(c => [c.id, createInitialTileState()])),
  );
  const [showGames, setShowGames] = useState(true);
  const [running, setRunning] = useState(false);
  const [farmStartTime, setFarmStartTime] = useState<number | null>(null);
  const [tick, setTick] = useState(0); // forces re-render every 1s while running for elapsed clocks
  const iframeRefs = useRef<Record<string, HTMLIFrameElement | null>>({});

  // Listen for postMessage from child iframes
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const data = e.data;
      if (!data || typeof data !== 'object') return;
      if (typeof data.tile !== 'string') return;
      const tile = data.tile;
      if (data.type === 'farm-status' && data.status === 'starting') {
        setTiles(t => ({ ...t, [tile]: { ...t[tile], status: 'running', target: data.target ?? runsPerChar } }));
      } else if (data.type === 'farm-progress') {
        setTiles(t => ({
          ...t,
          [tile]: {
            ...t[tile],
            status: 'running',
            completed: data.completed ?? 0,
            target: data.target ?? t[tile].target,
            inFlightSurv: data.inFlightSurv ?? 0,
            elapsedSec: data.elapsedSec ?? 0,
            fps: data.fps ?? 0,         // BETA 13.85
            memMB: data.memMB ?? 0,     // BETA 13.85
          },
        }));
      } else if (data.type === 'farm-complete') {
        setTiles(t => ({
          ...t,
          [tile]: { ...t[tile], status: 'complete', completed: data.runCount ?? t[tile].completed, csv: data.csv },
        }));
      } else if (data.type === 'farm-error') {
        setTiles(t => ({
          ...t,
          [tile]: { ...t[tile], status: 'error', error: data.error },
        }));
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [runsPerChar]);

  // 1Hz tick for live elapsed-time display while farm is running
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  const startFarm = useCallback(() => {
    setTiles(Object.fromEntries(CHARACTERS.map(c => [c.id, createInitialTileState()])));
    setRunning(true);
    setFarmStartTime(Date.now());
    setAutoDownloaded(false); // BETA 13.85: re-arm auto-download for the new run
    // BETA 13.85: iframe `key` includes farmStartTime → React remounts every iframe
    // on each START click, guaranteeing fresh game state, fresh bot.allRuns, no
    // stale data leaking between farm sessions.
  }, []);

  // BETA 13.85 — track whether we already auto-fired the master CSV download for
  // this farm session so we don't loop it.
  const [autoDownloaded, setAutoDownloaded] = useState(false);

  const stopFarm = useCallback(() => {
    // BETA 13.100c — partial-save on STOP. Previously stopFarm just unmounted
    // the iframes, throwing away any runs that had completed in tiles which
    // hadn't hit their target yet. User feedback: "if i stop early i want it
    // to still save what it was". Now we broadcast `farm-stop-request` to all
    // running iframes; each responds with `farm-complete` carrying whatever
    // partial bot.allRuns it has. We wait briefly for those messages to land,
    // then auto-download the master CSV before unmounting.
    let any = false;
    for (const id of Object.keys(iframeRefs.current)) {
      const f = iframeRefs.current[id];
      if (f?.contentWindow) {
        f.contentWindow.postMessage({ type: 'farm-stop-request' }, '*');
        any = true;
      }
    }
    if (!any) {
      setRunning(false);
      setFarmStartTime(null);
      return;
    }
    // Wait 700ms for iframes to flush their partial CSV via farm-complete
    // (the existing message handler at line ~85 saves csv into tile state).
    setTimeout(() => {
      // Trigger download from whatever tiles produced csv data — partial or full
      downloadMasterCSVInternal();
      setAutoDownloaded(true); // prevent the all-tiles-complete effect from double-firing
      setRunning(false);
      setFarmStartTime(null);
    }, 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // BETA 13.85 — auto-download master CSV when all tiles complete (one-shot per farm session)
  useEffect(() => {
    if (autoDownloaded) return;
    const completedTiles = Object.values(tiles).filter(t => t.status === 'complete' && t.csv);
    const activeCount = Object.values(enabled).filter(Boolean).length;
    if (completedTiles.length > 0 && completedTiles.length === activeCount && running) {
      // All active tiles done — fire the download
      downloadMasterCSVInternal();
      setAutoDownloaded(true);
    }
    // downloadMasterCSVInternal is referenced via closure below; intentionally not in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiles, enabled, running, autoDownloaded]);

  const downloadMasterCSVInternal = () => {
    const completedTiles = Object.values(tiles).filter(t => t.status === 'complete' && t.csv);
    if (completedTiles.length === 0) return;
    const firstCsv = completedTiles[0].csv!;
    const headerLine = firstCsv.split('\n')[0];
    const allDataRows: string[] = [];
    for (const t of completedTiles) {
      const lines = t.csv!.split('\n');
      allDataRows.push(...lines.slice(1).filter(l => l.trim().length > 0));
    }
    const master = [headerLine, ...allDataRows].join('\n');
    const blob = new Blob([master], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `bot_farm_master_${allDataRows.length}runs_${Date.now()}.csv`;
    a.click();
  };

  const downloadMasterCSV = useCallback(() => {
    // Manual click → same logic as auto-download. BETA 13.85: deduped via shared helper.
    downloadMasterCSVInternal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiles]);

  const activeChars = CHARACTERS.filter(c => enabled[c.id]);
  const totalDone = Object.values(tiles).filter(t => t.status === 'complete').length;
  const totalRunsCompleted = Object.values(tiles).reduce((sum, t) => sum + t.completed, 0);
  const totalRunsTarget = activeChars.length * runsPerChar;
  const allComplete = activeChars.length > 0 && totalDone === activeChars.length;
  const elapsedSec = farmStartTime ? Math.floor((Date.now() - farmStartTime) / 1000) : 0;

  // Layout: 5×2 grid optimized for 5140×1440 ultrawide. Each tile ~1028×720.
  // CSS grid handles the auto-tiling responsively; on smaller screens it adapts.

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      color: '#e6e6e6',
      fontFamily: 'monospace',
      padding: 12,
      boxSizing: 'border-box',
    }}>
      {/* Top control bar */}
      <div style={{
        display: 'flex',
        gap: 16,
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: '8px 12px',
        background: '#141414',
        border: '1px solid #2a2a2a',
        borderRadius: 6,
        marginBottom: 8,
      }}>
        <div style={{ fontWeight: 700, fontSize: 18, color: '#0c4' }}>🤖 BOT FARM</div>
        <div style={{ color: '#888', fontSize: 12 }}>BETA 13.84</div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            Runs/char:
            <input
              type="number"
              min={1}
              max={20}
              value={runsPerChar}
              disabled={running}
              onChange={e => setRunsPerChar(Math.max(1, Math.min(20, parseInt(e.target.value) || 5)))}
              style={{ width: 50, background: '#0a0a0a', border: '1px solid #333', color: '#e6e6e6', padding: '2px 6px', borderRadius: 3 }}
            />
          </label>

          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" checked={showGames} onChange={e => setShowGames(e.target.checked)} />
            Show games
          </label>

          {!running ? (
            <button onClick={startFarm}
              style={{ background: '#0a3', border: '1px solid #0c4', color: '#fff', padding: '6px 16px', fontWeight: 700, cursor: 'pointer', borderRadius: 4 }}>
              ▶ START FARM ({activeChars.length} chars × {runsPerChar} = {totalRunsTarget} runs)
            </button>
          ) : (
            <button onClick={stopFarm}
              style={{ background: '#a30', border: '1px solid #c40', color: '#fff', padding: '6px 16px', fontWeight: 700, cursor: 'pointer', borderRadius: 4 }}>
              ⏹ STOP
            </button>
          )}

          <button
            onClick={downloadMasterCSV}
            disabled={totalDone === 0}
            style={{
              background: allComplete ? '#0a3' : (totalDone > 0 ? '#332' : '#222'),
              border: `1px solid ${allComplete ? '#0c4' : '#444'}`,
              color: totalDone > 0 ? '#fff' : '#666',
              padding: '6px 16px',
              fontWeight: 700,
              cursor: totalDone > 0 ? 'pointer' : 'not-allowed',
              borderRadius: 4,
            }}>
            ⬇ DOWNLOAD MASTER CSV ({totalRunsCompleted} runs)
          </button>
        </div>
      </div>

      {/* Per-character enable checkboxes (only when not running) */}
      {!running && (
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap', padding: '6px 12px',
          background: '#141414', border: '1px solid #2a2a2a', borderRadius: 6, marginBottom: 8, fontSize: 12,
        }}>
          <span style={{ color: '#888', marginRight: 8 }}>Run:</span>
          {CHARACTERS.map(c => (
            <label key={c.id} style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={enabled[c.id]}
                onChange={e => setEnabled(en => ({ ...en, [c.id]: e.target.checked }))}
              />
              {c.label} <span style={{ color: '#666' }}>({c.build})</span>
            </label>
          ))}
        </div>
      )}

      {/* Live status bar (visible while running OR after complete so you can see results) */}
      {(running || totalDone > 0) && (
        <div style={{
          display: 'flex', gap: 12, padding: '6px 12px',
          background: '#141414', border: '1px solid #2a2a2a', borderRadius: 6, marginBottom: 8, fontSize: 13,
        }}>
          <div>⏱ Elapsed: <strong>{Math.floor(elapsedSec / 60)}m {elapsedSec % 60}s</strong></div>
          <div>📊 Progress: <strong>{totalRunsCompleted} / {totalRunsTarget}</strong> runs</div>
          <div>✓ Complete: <strong>{totalDone} / {activeChars.length}</strong> chars</div>
          {allComplete && <div style={{ color: '#0c4', fontWeight: 700 }}>🎉 FARM COMPLETE — download ready!</div>}
          {/* swallow the tick var to silence unused-warning (it's used to force re-render) */}
          <div style={{ display: 'none' }}>{tick}</div>
        </div>
      )}

      {/* The 5×2 game grid — only renders iframes when farm is running */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gridTemplateRows: 'repeat(2, 1fr)',
        gap: 8,
        // On 5140×1440 ultrawide, this renders ~1028×680 per tile after the chrome of header bars.
        height: 'calc(100vh - 200px)',
        minHeight: 720,
      }}>
        {CHARACTERS.map(c => {
          const tileState = tiles[c.id];
          const isActive = enabled[c.id];
          const shouldRender = running && isActive;
          const iframeSrc = shouldRender
            ? `/?botfarm=true&character=${c.id}&build=${c.build}&runs=${runsPerChar}&tile=${c.id}&muted=true`
            : 'about:blank';
          return (
            <div key={c.id} style={{
              border: `2px solid ${
                tileState.status === 'complete' ? '#0c4' :
                tileState.status === 'running' ? '#0c8' :
                tileState.status === 'error' ? '#c33' : '#222'
              }`,
              borderRadius: 6,
              background: '#0a0a0a',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              position: 'relative',
            }}>
              {/* Per-tile header */}
              <div style={{
                padding: '4px 8px',
                background: '#141414',
                borderBottom: '1px solid #2a2a2a',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontSize: 11,
              }}>
                <span style={{ fontWeight: 700 }}>{c.label}</span>
                <span style={{ color: '#888' }}>{c.build}</span>
                <span style={{
                  color:
                    tileState.status === 'complete' ? '#0c4' :
                    tileState.status === 'running' ? '#0c8' :
                    tileState.status === 'error' ? '#c33' : '#666',
                }}>
                  {tileState.status === 'complete' ? `✓ ${tileState.completed}/${tileState.target}` :
                   tileState.status === 'running' ? `${tileState.completed}/${tileState.target} · ${tileState.inFlightSurv}s` :
                   tileState.status === 'error' ? `❌ ${tileState.error?.slice(0, 20)}` :
                   isActive ? 'idle' : 'disabled'}
                </span>
              </div>
              {/* Iframe holding the actual game (or placeholder) */}
              <div style={{ flex: 1, display: showGames ? 'block' : 'none', position: 'relative' }}>
                {shouldRender ? (
                  <iframe
                    // BETA 13.85: key includes farmStartTime → React remounts the iframe
                    // on each START FARM click, guaranteeing fresh game state, fresh
                    // bot.allRuns, no stale data leaking between farm sessions.
                    key={`${c.id}-${farmStartTime}`}
                    ref={el => { iframeRefs.current[c.id] = el; }}
                    src={iframeSrc}
                    title={c.label}
                    style={{ width: '100%', height: '100%', border: 'none', background: '#000' }}
                  />
                ) : (
                  <div style={{
                    width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#444', fontSize: 13,
                  }}>
                    {isActive ? 'press START to launch' : 'disabled'}
                  </div>
                )}
              </div>
              {!showGames && shouldRender && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontSize: 11 }}>
                  (hidden — running headless)
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* BETA 13.85 — PERFORMANCE PANEL (left-middle, fixed). User asked: "there
          needs to be something on screen for me to tell if this is actually running
          on my hardware and not bottlenecks cause its on a web browser. when i was
          it all games were already running at 25-30fps which i would prefer that
          ui to be on the left middle of the screen". Shows per-tile FPS + aggregate
          + memory so the user can SEE if their M2 Pro/Max is being pushed or if
          Chrome is throttling. Color-coded:
            green ≥50fps · yellow 25-49 · red <25 (browser-throttled or CPU-pegged) */}
      {running && (() => {
        const activeStates = CHARACTERS.filter(c => enabled[c.id]).map(c => ({ c, s: tiles[c.id] }));
        const totalFps = activeStates.reduce((sum, x) => sum + (x.s?.fps ?? 0), 0);
        const avgFps = activeStates.length > 0 ? Math.round(totalFps / activeStates.length) : 0;
        const totalMem = activeStates.reduce((sum, x) => sum + (x.s?.memMB ?? 0), 0);
        const fpsColor = (fps: number) => fps >= 50 ? '#0c4' : fps >= 25 ? '#fc4' : '#f44';
        return (
          <div style={{
            position: 'fixed',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(10, 10, 10, 0.92)',
            border: '1px solid #2a2a2a',
            borderRadius: 6,
            padding: '10px 12px',
            fontFamily: 'monospace',
            fontSize: 11,
            color: '#e6e6e6',
            minWidth: 180,
            zIndex: 1000,
            boxShadow: '0 0 12px rgba(0,255,128,0.15)',
          }}>
            <div style={{ fontWeight: 700, marginBottom: 6, color: '#0c4', letterSpacing: 0.5 }}>⚡ HARDWARE LOAD</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
              <span style={{ color: '#888' }}>aggregate FPS:</span>
              <span style={{ color: fpsColor(avgFps), fontWeight: 700 }}>{totalFps} ({avgFps}/tile)</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: '#888' }}>JS heap (Chrome):</span>
              <span style={{ color: '#cc8' }}>{totalMem} MB</span>
            </div>
            <div style={{ borderTop: '1px solid #222', paddingTop: 6 }}>
              {activeStates.map(({c, s}) => (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
                  <span style={{ color: '#aaa' }}>{c.label.slice(0, 9)}:</span>
                  <span style={{ color: fpsColor(s?.fps ?? 0), fontWeight: 600 }}>{s?.fps ?? 0} fps</span>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid #222', marginTop: 6, paddingTop: 4, fontSize: 10, color: '#666' }}>
              <div>green = healthy (≥50)</div>
              <div>yellow = throttled (25-49)</div>
              <div>red = bottlenecked (&lt;25)</div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
